/* TODO: t() */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { postJSON } from '../../support/Fetch';
import { isValidEmail } from '../../core/support/isValidEmail';
import isPasswordStrengthEnough from '../../support/shared/isPasswordStrengthEnough';
import { Account } from '../../types/shared/Account';
import { VendorAccount } from '../../types/shared/VendorAccount';
import { showError, showSuccess } from '../App/AppAlert';
import { canUser } from '../../support/shared/permissionHelpers';
import { showDialog } from '../App/AppDialog';
import LoadingIndicator from '../uiold/LoadingIndicator';
import VendorAccountActions from '../../support/VendorAccountActions';
import {
  Button,
  ButtonToolbar,
  DummyLogin,
  Form,
  FormField,
  Text,
  TextInputPassword,
  TextInputWithError,
} from '../uiold';
import { themeColors } from '../uiold/Constants';
import { User } from '../../types/shared/User';

type Props = {
  account: Account;
  user: User | null | undefined;
  vendorAccount: VendorAccount | null | undefined;
  onClose: () => void;
  selectedID: string | null | undefined;
};

let passwordError =
  'Password must be at least 8 characters including uppercase, lowercase, and number.';

function VendorAccountDetailsContainer(props: Props) {
  let { vendorAccount, selectedID, onClose, account, user } = props;
  if (!vendorAccount) {
    return (
      <View style={styles.emptyState}>
        <Text style={styles.emptyStateText}>
          Select a vendor account from the list
        </Text>
      </View>
    );
  }
  return (
    <VendorAccountDetails
      key={selectedID}
      vendorAccount={vendorAccount}
      onClose={onClose}
      account={account}
      user={user}
    />
  );
}

type FieldKey = 'name' | 'email' | 'password';

type FormData = typeof initialFormState;

let initialFormState = {
  name: '',
  email: '',
  password: '',
};

function VendorAccountDetails(props: {
  account: Account;
  vendorAccount: VendorAccount | null;
  onClose: () => void;
  user: User | null | undefined;
}) {
  let { vendorAccount, onClose, user } = props;
  let [formData, setFormData] = useState<FormData>({
    name: vendorAccount?.name ?? initialFormState.name,
    email: vendorAccount?.email ?? initialFormState.email,
    password: initialFormState.password,
  });
  let [isLoading, setIsLoading] = useState(false);
  let userCan = canUser(user, { vendors: 'WRITE' });

  let onSave = async () => {
    if (!vendorAccount) {
      return;
    }
    let checkData = checkVendorAccountFormData(formData, vendorAccount);
    if (checkData?.errorMessage) {
      showError(checkData.errorMessage);
      return;
    }
    setIsLoading(true);
    let vendorId = vendorAccount.vendor;
    let url = `/api/vendor-accounts/${vendorId}`;
    let result = await postJSON(url, { vendorAccount: formData });
    setIsLoading(false);
    if (result?.success === true) {
      // TODO: Add something to update vendorAccount datastore
      VendorAccountActions.updateVendorAccountById(vendorAccount.id, formData);
      showSuccess('Saved successfully');
      onClose();
    } else {
      showError('Something went wrong');
    }
  };

  let onChange = (key: FieldKey, value: string | boolean) => {
    setFormData({ ...formData, [key]: value });
  };

  let onDeletePress = () => {
    showDialog(
      'Delete Vendor Account?',
      'Are you sure you want to permanently delete this Vendor Account?',
      ['Cancel', 'Delete'],
      (value) => {
        if (value === 'Cancel') {
          return;
        }
        deleteVendorAccount();
        onClose();
      },
    );
  };

  const generateOTP = async () => {
    if (!vendorAccount) {
      return;
    }
    setIsLoading(true);
    const vendorId = vendorAccount.vendor;
    let result = await postJSON(
      `/api/vendor-accounts/${vendorId}/generate-otp`,
      {},
    );
    setIsLoading(false);
    if (result.success === true) {
      showDialog(
        'New One Time Password',
        [
          `Your one-time password is: ${result.password}`,
          `This password can only be used once and will expire in five minutes.`,
        ].join('\n'),
      );
    } else {
      showDialog('Error', 'Unable to create password');
    }
  };

  let deleteVendorAccount = async () => {
    if (!vendorAccount) {
      return;
    }
    setIsLoading(true);
    let vendorId = vendorAccount.vendor;
    let url = `/api/vendor-accounts/${vendorId}/delete`;
    let result = await postJSON(url, {});
    if (result.success === true) {
      VendorAccountActions.deleteVendorAccountById(vendorAccount.id);
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <View style={styles.root}>
        <LoadingIndicator />
      </View>
    );
  }
  return (
    <View style={styles.root}>
      <View style={styles.header}>
        {vendorAccount && (
          <Text style={styles.headingTwo}>Edit Vendor Account</Text>
        )}

        {userCan && (
          <ButtonToolbar style={styles.toolbar}>
            <Button
              title="Delete"
              onPress={onDeletePress}
              style={styles.deleteButton}
            />
            <Button
              title="Cancel"
              onPress={onClose}
              style={styles.cancelButton}
            />
            <Button title="Save" onPress={onSave} isPrimary />
          </ButtonToolbar>
        )}
      </View>
      <Form onSubmit={onSave} autoComplete="off" style={styles.flex}>
        <ScrollView style={styles.scrollView}>
          <View style={styles.formBody}>
            {/* Dummy fields to prevent Safari autofill. */}
            <DummyLogin />
            <FormField
              render={(id) => (
                <View style={styles.formField}>
                  <Text accessibilityRole="label" htmlFor={id}>
                    Name
                  </Text>
                  <TextInputWithError
                    errorMessage="This field must have a valid name"
                    id={id}
                    value={formData.name}
                    checkError={(value) => value.length > 1}
                    onChangeText={(value) => onChange('name', value)}
                  />
                </View>
              )}
            />
            <FormField
              render={(id) => (
                <View style={styles.formField}>
                  <Text accessibilityRole="label" htmlFor={id}>
                    Email
                  </Text>
                  <TextInputWithError
                    errorMessage="Must be a valid email address"
                    keyboardType="email-address"
                    id={id}
                    value={formData.email}
                    checkError={(value) => isValidEmail(value)}
                    onChangeText={(value) => onChange('email', value)}
                  />
                </View>
              )}
            />

            <FormField
              render={(id) => (
                <View style={styles.formField}>
                  <Text accessibilityRole="label" htmlFor={id}>
                    Password
                  </Text>
                  <TextInputPassword
                    id={id}
                    skipFirstCheck
                    value={formData.password}
                    checkPassword={(value) =>
                      onCheckPassword(value, vendorAccount)
                    }
                    placeholder="Leave blank to keep the old password"
                    errorMessage={passwordError}
                    onChangeText={(value) => onChange('password', value)}
                  />
                </View>
              )}
            />
            {userCan && (
              <Button
                title="Generate one-time password"
                onPress={() => generateOTP()}
                style={{ marginVertical: 10, alignSelf: 'flex-start' }}
              />
            )}
          </View>
        </ScrollView>
      </Form>
    </View>
  );
}

function onCheckPassword(value: string, vendorAccount?: VendorAccount | null) {
  if (vendorAccount && value.length === 0) {
    return true;
  } else {
    return isPasswordStrengthEnough(value);
  }
}

function checkVendorAccountFormData(
  formData: FormData,
  vendorAccount?: VendorAccount | null,
) {
  let { name, email, password } = formData;
  let errorMessageArray = [];
  if (name.length < 1) {
    errorMessageArray.push('You require a name');
  }
  if (!isValidEmail(email)) {
    errorMessageArray.push('You need a valid email');
  }
  if (!onCheckPassword(password, vendorAccount)) {
    errorMessageArray.push(passwordError);
  }
  if (errorMessageArray.length > 0) {
    return { success: false, errorMessage: errorMessageArray.join('\n') };
  }
  return { success: true };
}

let styles = StyleSheet.create({
  root: {
    flexGrow: 1,
    backgroundColor: themeColors.white,
    borderRadius: 5,
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.withOpacity.blue50,
  },
  header: {
    paddingVertical: 20,
    marginHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: themeColors.withOpacity.blue50,
  },
  deleteButton: {
    borderColor: themeColors.red,
  },
  headingTwo: {
    fontSize: 16,
    lineHeight: 22,
    padding: 0,
    color: '#444',
    fontWeight: '600',
  },
  scrollView: {
    flex: 1,
    height: 0,
  },
  flex: {
    flex: 1,
  },
  formBody: {
    flex: 1,
    justifyContent: 'space-between',
    paddingHorizontal: 20,
  },
  formField: {
    marginVertical: 10,
  },
  cancelButton: {
    borderWidth: StyleSheet.hairlineWidth,
    backgroundColor: themeColors.backgroundGrey,
  },
  toolbar: {
    paddingVertical: 10,
    justifyContent: 'flex-end',
  },
  emptyState: {
    padding: 40,
  },
  emptyStateText: {
    textAlign: 'center',
  },
});

export default VendorAccountDetailsContainer;
